/**
 * Box 11 (one row per channel) and box 12 (one row per ad account): Spend,
 * Impressions, Clicks, CTR, CPC, CPM (spec §3.3). Box 11 has no share column
 * (box 5 holds share). Rows are already one currency. Pure.
 *
 * @module services/overview/breakdownTables
 */
import { cpcOf, cpmOf, ctrOf, groupRows, isLowSample, totalsOf } from './overviewMath';
import type { FactRow } from './overviewTypes';

export interface MetricRow {
  key: string;
  /** Channel id (box 11) or account name (box 12; its id prefix when it has no name). */
  label: string;
  platform: string;
  currency: string | null;
  spend: number | null;
  impressions: number | null;
  clicks: number | null;
  ctr: number | null;
  cpc: number | null;
  cpm: number | null;
  lowSample: boolean;
}

const rowOf = (key: string, label: string, own: readonly FactRow[]): MetricRow => {
  const t = totalsOf(own);
  return {
    key, label, platform: own[0].platform, currency: own[0].currency,
    spend: t.spend, impressions: t.impressions, clicks: t.clicks,
    ctr: ctrOf(t), cpc: cpcOf(t), cpm: cpmOf(t), lowSample: isLowSample(t),
  };
};

/** Biggest spend first; an unreported spend sorts last; ties by label. */
const bySpend = (a: MetricRow, b: MetricRow): number => (b.spend ?? -1) - (a.spend ?? -1) || a.label.localeCompare(b.label);

export const channelRowsOf = (rows: readonly FactRow[]): MetricRow[] =>
  groupRows(rows, (r) => r.platform).map(([platform, own]) => rowOf(platform, platform, own)).sort(bySpend);

export const accountRowsOf = (rows: readonly FactRow[]): MetricRow[] =>
  groupRows(rows, (r) => `${r.workspaceId}|${r.platform}|${r.accountId ?? ''}`)
    .map(([key, own]) => rowOf(key, own[0].accountName ?? own[0].accountId?.slice(0, 8) ?? '—', own))
    .sort(bySpend);
